/**
 * One place decides what a price looks like. The tile caption under a photo
 * and the panel's own field sit in different features and have to agree, and
 * a board of five hundred tiles formats a price per tile per render — so the
 * formatters are built once per currency and kept.
 */

/**
 * No stored currency means dollars. Every price in here was typed by one
 * person in one country; stage 4 is where a listing starts saying what it
 * charges in, and until then a bare number reads as an error.
 */
const DEFAULT = 'USD';

const formatters = new Map<string, Intl.NumberFormat | undefined>();

/** Grouped and symboled — "$1,299.50". For display, never for a field. */
export function formatPrice(
  price: number | undefined,
  currency: string | undefined,
): string | undefined {
  if (price === undefined) return undefined;

  const format = formatter(currency);
  // A store can hand back something that isn't an ISO code. Showing the code
  // beside the number is wrong-looking but honest; a dollar sign would be a
  // lie about what the thing cost.
  if (format === undefined) return `${price} ${currency ?? ''}`.trim();

  return format.format(price);
}

/** Just the symbol. The panel's field holds the raw number beside it. */
export function currencySymbol(currency: string | undefined): string {
  const format = formatter(currency);
  if (format === undefined) return currency ?? '$';

  const part = format.formatToParts(0).find((piece) => piece.type === 'currency');
  return part?.value ?? '$';
}

function formatter(currency: string | undefined): Intl.NumberFormat | undefined {
  const code = currency ?? DEFAULT;

  if (!formatters.has(code)) {
    try {
      formatters.set(code, new Intl.NumberFormat(undefined, { style: 'currency', currency: code }));
    } catch {
      formatters.set(code, undefined);
    }
  }

  return formatters.get(code);
}
